import { Pencil, Trash2, Wallet } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { SuppressedValue } from '@/components/data/SuppressedValue'
import { IconButton } from '@/components/ui/IconButton'
import { Input } from '@/components/ui/Input'
import { CarrierSelect } from '@/features/cases/components/CarrierSelect'
import { cn } from '@/lib/cn'
import { formatMoney, formatNumber } from '@/lib/format'
import type { CaseLeg, VehicleMoney } from '@/types/domain'

import type { VehicleRow } from './VehiclesTable'

type Props = {
  row: VehicleRow
  index: number
  vehicle?: CaseLeg['vehicles'][number]
  money?: VehicleMoney
  identityHidden: boolean
  pending: boolean
  canPay: boolean
  problems: { rate: boolean; weight: boolean }
  onChange: (patch: Partial<VehicleRow>) => void
  onRemove: () => void
  onPay: () => void
}

/** Six reading columns; the full set of carriage facts opens underneath its
 * truck. Money remains read-only and identity suppression applies to every
 * grouped line, just as it did to the former separate columns.
 *
 * The trailing column carries the row's three acts as icons — pay, edit,
 * delete — each named to a screen reader AND carrying the same words as a
 * hover title, which is the only reason an icon may stand alone on the two
 * that are financial and destructive (DESIGN.md § Icons). They sit together
 * on every row rather than being scattered between the row and its opened
 * panel: a reader deleting the wrong truck was, before this, one who had to
 * open a truck to find the trash at all. */
export function VehicleTableRows({ row, index, vehicle, money, identityHidden, pending, canPay, problems, onChange, onRemove, onPay }: Props) {
  const { t } = useTranslation()
  const col = (name: 'plate' | 'trailer' | 'container' | 'cmr' | 'carrier' | 'driver' | 'phone' | 'weight' | 'rate') => t(`cases.legs.vehiclesTable.columns.${name}`)
  const editLabel = t('cases.legs.vehiclesTable.editRow', { n: index + 1 })
  const plate = (vehicle?.plate ?? row.plate.trim()) || `#${index + 1}`
  const cell = 'px-3 py-4 align-top md:first:pl-6 md:last:pr-6'
  const figure = (value: VehicleMoney['agreed'] | undefined) => value ? formatMoney(value.amount, value.currency) : '—'

  return (
    <>
      <tr className={cn('border-line grid grid-cols-2 border-b text-[13px] last:border-b-0 md:table-row', row.editing && 'bg-surface-selected')}>
        <td className={cn(cell, 'col-span-2 max-md:pb-1 md:w-[20%]')}>
          {row.editing || identityHidden ? (
            <span className="text-text-heading font-semibold">{row.plate || t('cases.legs.vehiclesTable.newTruck')}</span>
          ) : (
            <button type="button" aria-label={editLabel} onClick={() => onChange({ editing: true })}
              className="text-text-heading hover:text-text-link cursor-pointer text-left font-semibold underline-offset-4 hover:underline">
              {row.plate}
            </button>
          )}
          <div className="text-text-muted mt-1 space-y-0.5 text-xs leading-relaxed">
            {row.trailerPlate ? <p>{col('trailer')} {row.trailerPlate}</p> : null}
            {row.cmrNumber ? <p>{col('cmr')} {row.cmrNumber}</p> : null}
            {row.containerNumber ? <p className="break-all">{col('container')} {row.containerNumber}</p> : null}
          </div>
        </td>
        <td className={cn(cell, 'col-span-2 max-md:py-2 md:w-[27%]')}>
          <div className="text-text-heading font-medium leading-snug [overflow-wrap:anywhere]">
            <SuppressedValue value={vehicle?.carrierName ?? row.carrierName} label={t('common.suppressed.carrier')} />
          </div>
          {vehicle?.driverName || row.driverName ? <p className="text-text-muted mt-1 text-xs leading-relaxed">
            {col('driver')}: <SuppressedValue value={vehicle?.driverName ?? row.driverName} label={t('common.suppressed.generic')} />
          </p> : null}
          {vehicle?.driverPhone || row.driverPhone ? <p className="text-text-muted mt-0.5 text-xs break-all">
            <SuppressedValue value={vehicle?.driverPhone ?? row.driverPhone} label={t('common.suppressed.generic')} />
          </p> : null}
        </td>
        <td className={cn(cell, 'asa-num whitespace-nowrap md:text-right')}>
          <span className="text-text-muted mb-1 block text-[11px] md:hidden">{col('weight')}</span>
          {row.weightKg ? formatNumber(row.weightKg) : '—'}
        </td>
        <td className={cn(cell, 'asa-num whitespace-nowrap md:text-right')}>
          <span className="text-text-muted mb-1 block text-[11px] md:hidden">{col('rate')}</span>
          <span className="text-text-heading font-semibold">{figure(money?.agreed)}</span>
          {money ? <p className="text-text-muted mt-1 text-xs">{t('cases.legs.vehiclesTable.columns.advance')} {figure(money.advance)}</p> : null}
        </td>
        <td className={cn(cell, 'asa-num whitespace-nowrap md:text-right')}>
          <span className="text-text-muted mb-1 block text-[11px] md:hidden">{t('cases.legs.vehiclesTable.columns.paid')}</span>
          <span className="text-text-heading font-semibold">{figure(money?.paid)}</span>
        </td>
        <td className={cn(cell, 'md:text-right')}>
          <div className="flex items-center justify-end gap-0.5">
            {/* Paying is offered on a SAVED truck only, and only while the
                direction has a money block to seed the sheet from — the same
                two conditions the text button carried. */}
            {row.id !== '' && canPay ? (
              <IconButton size="sm" icon={Wallet} onClick={onPay}
                label={t('cases.legs.vehiclesTable.payRowNamed', { plate: vehicle?.plate ?? row.plate })} />
            ) : null}
            <IconButton size="sm" icon={Pencil} disabled={identityHidden || pending}
              aria-expanded={row.editing}
              label={t('cases.legs.vehiclesTable.editRowNamed', { plate })}
              onClick={() => onChange({ editing: !row.editing })} />
            <IconButton size="sm" icon={Trash2} disabled={identityHidden || pending}
              className="hover:text-negative"
              label={t('cases.legs.vehiclesTable.removeRowNamed', { plate })}
              onClick={onRemove} />
          </div>
        </td>
      </tr>
      {row.editing ? (
        <tr className="bg-surface-selected border-line block border-b md:table-row">
          <td colSpan={6} className="block px-4 py-4 md:table-cell md:px-6">
            <div className="grid gap-x-4 gap-y-3 sm:grid-cols-2 xl:grid-cols-4">
              {([
                ['plate', 'plate'], ['trailerPlate', 'trailer'], ['containerNumber', 'container'], ['cmrNumber', 'cmr'],
              ] as const).map(([field, name]) => <label key={field} className="flex min-w-0 flex-col gap-1.5">
                <span className="text-text-muted text-xs">{col(name)}</span>
                <Input inputSize="sm" value={row[field]} aria-label={col(name)} onChange={(event) => onChange({ [field]: event.target.value })} />
              </label>)}
              <div className="flex min-w-0 flex-col gap-1.5">
                <span className="text-text-muted text-xs">{col('carrier')}</span>
                <CarrierSelect size="sm" value={row.carrierId} name={row.carrierName}
                  onChange={(carrier) => onChange({ carrierId: carrier.id, carrierName: carrier.name })} />
              </div>
              {(['driverName', 'driverPhone'] as const).map((field) => <label key={field} className="flex min-w-0 flex-col gap-1.5">
                <span className="text-text-muted text-xs">{col(field === 'driverName' ? 'driver' : 'phone')}</span>
                <Input inputSize="sm" value={row[field]} inputMode={field === 'driverPhone' ? 'tel' : undefined}
                  aria-label={col(field === 'driverName' ? 'driver' : 'phone')} onChange={(event) => onChange({ [field]: event.target.value })} />
              </label>)}
              <label className="flex min-w-0 flex-col gap-1.5">
                <span className="text-text-muted text-xs">{col('weight')}</span>
                <Input inputSize="sm" inputMode="numeric" className="asa-num" value={row.weightKg} aria-label={col('weight')}
                  aria-invalid={problems.weight || undefined} onChange={(event) => onChange({ weightKg: event.target.value })} />
              </label>
              {row.id === '' ? <label className="flex min-w-0 flex-col gap-1.5">
                <span className="text-text-muted text-xs">{col('rate')}</span>
                <Input inputSize="sm" inputMode="decimal" className="asa-num" value={row.rate} aria-label={col('rate')}
                  aria-invalid={problems.rate || undefined} onChange={(event) => onChange({ rate: event.target.value })} />
              </label> : null}
            </div>
          </td>
        </tr>
      ) : null}
    </>
  )
}
